// Mechanical Maintenance Management — the back-office view of the tech
// mech-schedule flow.
//
// Rather than maintaining a desktop twin of the mobile-optimized
// /mech-schedule pages, we frame the live pages (same origin) inside
// the hub. Office staff keep the sidebar; clicking into buildings,
// visits and checklists all happens inside the frame, and any edit
// made here is instantly what the techs see — one implementation.

import './InternalMobile.css';
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaExternalLinkAlt, FaLink, FaCheck, FaClipboardList } from 'react-icons/fa';
import { useMaintenanceAccess } from './MaintenanceAccessContext';
import { categoriesForRole, CAT_ADMIN } from './maintenanceAccess';
import MechEquipmentScopesModal from './MechEquipmentScopesModal';

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

export default function MaintenanceMechManagement() {
  const [copied, setCopied] = useState(false);
  const [scopesOpen, setScopesOpen] = useState(false);
  const { role } = useMaintenanceAccess();
  const canSeeJobberLinks = categoriesForRole(role).has(CAT_ADMIN);
  const [searchParams] = useSearchParams();
  const buildingId = searchParams.get('building');
  const schedulePath = buildingId ? `/mech-schedule/buildings/${encodeURIComponent(buildingId)}` : '/mech-schedule';

  const copyTechLink = async () => {
    const url = `${window.location.origin}/mech-schedule`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API blocked (e.g. non-HTTPS) — fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="maintenance-mechanical-frame" style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: sharedFont }}>
      {/* Slim header so it's clear you're looking at the live tech tool */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 24px', background: '#fff', borderBottom: '1px solid #E6E8EC',
      }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: '#1a2236' }}>Mechanical Maintenance</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>
            Live view of the tech schedule — anything you edit here is exactly what techs see on their phones
          </div>
        </div>
        <button
          type="button"
          onClick={copyTechLink}
          style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 13, fontWeight: 700,
            color: copied ? '#15803D' : '#1F3A68',
            background: copied ? '#F0FDF4' : '#fff', cursor: 'pointer',
            border: `1px solid ${copied ? '#BBF7D0' : '#E6E8EC'}`, borderRadius: 9,
            padding: '8px 14px', whiteSpace: 'nowrap', fontFamily: sharedFont,
            transition: 'color .15s ease, background .15s ease, border-color .15s ease',
          }}
        >
          {copied ? (
            <>Link copied <FaCheck style={{ fontSize: 11 }} /></>
          ) : (
            <>Copy link for technicians <FaLink style={{ fontSize: 11 }} /></>
          )}
        </button>
        {/* Writing the scope of work is office work, and it's read by techs
            inside the frame, so the editor belongs in the hub chrome. */}
        <button
          type="button"
          onClick={() => setScopesOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 13, fontWeight: 700, color: '#1F3A68', background: '#fff',
            border: '1px solid #E6E8EC', borderRadius: 9, padding: '8px 14px',
            whiteSpace: 'nowrap', fontFamily: sharedFont, cursor: 'pointer',
          }}
        >
          Equipment scopes <FaClipboardList style={{ fontSize: 11 }} />
        </button>
        {/* Only office staff can rewire a contract's master job, and only they
            see this. The schedule below is the same page techs load, so the
            entry point lives up here in the hub chrome rather than in the
            frame. */}
        {canSeeJobberLinks && (
          <Link
            to="/maintenance/mech-jobber-links"
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              fontSize: 13, fontWeight: 700, color: '#1F3A68', textDecoration: 'none',
              border: '1px solid #E6E8EC', borderRadius: 9, padding: '8px 14px',
              whiteSpace: 'nowrap', fontFamily: sharedFont,
            }}
          >
            Jobber links <FaLink style={{ fontSize: 11 }} />
          </Link>
        )}
        <a
          href={schedulePath}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 13, fontWeight: 700, color: '#1F3A68', textDecoration: 'none',
            border: '1px solid #E6E8EC', borderRadius: 9, padding: '8px 14px',
            whiteSpace: 'nowrap',
          }}
        >
          Open full screen <FaExternalLinkAlt style={{ fontSize: 11 }} />
        </a>
      </div>

      <iframe
        title="Mechanical maintenance schedule"
        src={schedulePath}
        style={{ flex: 1, width: '100%', border: 'none', display: 'block', background: '#F4F7FB' }}
      />

      <MechEquipmentScopesModal open={scopesOpen} onClose={() => setScopesOpen(false)} />
    </div>
  );
}
